import { PageHeader } from '@ant-design/pro-components';
import { useMutation } from '@apollo/client/react';
import { css } from '@emotion/css';
import { App, Button, Input, Radio, Select, Space, Steps, Typography, theme } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import type { Ordination } from '../../../@types/native-api/generated/authv1';
import type { CardReaderWithUrl } from '../../../@types/native-api/generated/crs';
import { MainContent } from '../../../components/MainContent';
import { useCurrentContextStore } from '../../../hooks/store/useCurrentContextStore.ts';
import { buildErrorDetails, ecardEnv, getCardSlot } from '../../../utils/ginoHelpers.ts';
import { openExternalLink } from '../../../utils/linkOpener.ts';
import { hasSomeRole, Role } from '../../../utils/user.ts';
import { type ConnectionState, EcardConnectionCheck } from './EcardConnectionCheck.tsx';
import { CREATE_ECARD_CONFIG_MUTATION } from './EcardSetupAssistant.gql';

type CardData = {
  name: string;
  nummer: string;
};

export const EcardSetupAssistant: React.FC<{ onHideAssistant: () => void }> = ({ onHideAssistant }) => {
  const { token } = theme.useToken();
  const [step, setStep] = useState(0);
  const [pin, setPin] = useState('');
  const [vpnr, setVpnr] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('pending');
  const [cardReaders, setCardReaders] = useState<CardReaderWithUrl[]>([]);
  const [ordis, setOrdis] = useState<Ordination[]>([]);
  const [cardReadersError, setCardReadersError] = useState('');
  const [cardReaderConnectionError, setCardReaderConnectionError] = useState('');
  const [cardReaderDataError, setCardReaderDataError] = useState('');
  const [pinError, setPinError] = useState('');
  const [cardReadersLoading, setCardReadersLoading] = useState(false);
  const [ordisLoading, setOrdisLoading] = useState(false);
  const [cardReader, setCardReader] = useState<CardReaderWithUrl | null>(null);
  const [ordi, setOrdi] = useState<Ordination | null>(null);
  const [taetigkeitsbereichId, setTaetigkeitsbereichId] = useState<string | null>(null);
  const [nameSuffix, setNameSuffix] = useState('');
  const [cardData, setCardData] = useState<CardData | null>(null);
  const { message } = App.useApp();
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const auth = useAuth();
  const isUserRole = hasSomeRole([Role.ROLE_LR_USER], auth.user);

  const [createEcardConfigMutation, { loading: createEcardConfigLoading }] = useMutation(CREATE_ECARD_CONFIG_MUTATION);

  const loadCardReaders = async () => {
    setCardReadersLoading(true);
    try {
      const response = await window.nativeApi?.ecardReaders(ecardEnv());

      if (!response?.error) {
        setCardReadersError('');
        setCardReaders(response?.data ?? []);
        setCardReaderConnectionError('');
        setStep(step => step + 1);
      } else {
        setCardReadersError(`Fehler beim Lesen der verfügbaren Kartenlesegeräte. ${buildErrorDetails(response)}`);
      }
    } finally {
      setCardReadersLoading(false);
    }
  };

  const checkCardReaderConnection = async () => {
    setCardReaderConnectionError('');

    const response = await window.nativeApi!.ginoStatus(cardReader?.checkIp ?? '', {});

    if (!response.error) {
      setCardReaderDataError('');
      setCardData(null);
      setStep(step => step + 1);
    } else {
      setCardReaderConnectionError(
        `Es ist ein Fehler bei der Kommunikation mit dem Lesegerät ${cardReader?.id} aufgetreten. ${buildErrorDetails(response)}`
      );
    }
  };

  const readCardData = async () => {
    const response = await window.nativeApi!.ginoStatus(cardReader?.checkIp ?? '', {});

    if (!response.error) {
      const slot = getCardSlot(response, 'o-card');
      if (!slot) {
        setCardData(null);
        setCardReaderDataError(
          'Keine Admin-Karte gefunden. Bitte stecken Sie Ihre Admin-Karte (o-card) in das Lesegerät und versuchen Sie es erneut.'
        );
        return;
      }
      setCardReaderDataError('');
      setCardData({
        name: `${slot.cardData?.titel ?? ''} ${slot.cardData?.vorname ?? ''} ${slot.cardData?.nachname ?? ''} ${slot.cardData?.titelHinten ?? ''}`,
        nummer: slot.cardData?.nummer ?? '',
      });
    } else {
      setCardData(null);
      setCardReaderDataError(
        `Es ist ein Fehler bei der Kommunikation mit dem Lesegerät ${cardReader?.id} aufgetreten. ${buildErrorDetails(response)}`
      );
    }
  };

  const readCardDataWithPin = async () => {
    setOrdisLoading(true);

    try {
      const response = await window.nativeApi!.ginoStatus(cardReader?.checkIp ?? '', { pin: pin, sigType: 'vp' });

      if (!response.error) {
        const slot = getCardSlot(response, 'o-card');
        if (!slot?.token?.value) {
          setVpnr('');
          setPinError(
            'Es konnte kein gültiges Zertifikat ausgestellt werden. Bitte stecken Sie Ihre Admin-Karte (o-card) in das Lesegerät und versuchen Sie es erneut.'
          );
          return;
        }

        const resp = await window.nativeApi!.ecardCreateDialog(ecardEnv(), slot.token.value, auth.user?.profile.sub ?? 'unknown');

        if (resp.error) {
          setVpnr('');
          setPinError(resp.error.text);
          return;
        }

        try {
          await window.nativeApi?.ecardCloseDialog(ecardEnv(), resp.data?.return?.dialogId ?? '');
        } catch (_e) {
          // ignore
        }

        setPinError('');
        setVpnr(slot.token.vpnr ?? '');
        setOrdis(resp.data?.return?.vp?.ordination ?? []);
        setTaetigkeitsbereichId(null);
        setOrdi(null);
        setStep(step => step + 1);
      } else {
        setPinError(
          `Es ist ein Fehler bei der Kommunikation mit dem Lesegerät ${cardReader?.id} aufgetreten. ${buildErrorDetails(response)}`
        );
      }
    } finally {
      setOrdisLoading(false);
    }
  };

  const createEcardConfig = async () => {
    try {
      await createEcardConfigMutation({
        variables: {
          input: {
            doctorId: isUserRole ? currentDoctorId : primaryDoctorId,
            cardPin: pin,
            vpnr: vpnr ?? '',
            cardReaderAddress: cardReader?.checkIp ?? '',
            cardReaderName: `${cardReader?.id ?? ''} ${nameSuffix}`.trim(),
            ordinationId: ordi?.ordinationId ?? '',
            ordinationName: `${cardData?.name} (${ordi?.strasse}, ${ordi?.postleitzahl} ${ordi?.stadt})`,
            taetigkeitsBereichId: taetigkeitsbereichId ?? '',
            taetigkeitsBereichName: ordi?.taetigkeitsBereich?.find(it => it.id === taetigkeitsbereichId)?.anzeigeText ?? '',
          },
        },
      });

      onHideAssistant();
    } catch {
      message.error('e-card Konfiguration konnte nicht angelegt werden');
    }
  };

  return (
    <MainContent size="medium">
      <PageHeader title="e-card Einrichtungsassistent" style={{ padding: 0, paddingBottom: 'inherit' }} />
      <Steps
        orientation="vertical"
        current={step}
        className={css`
          .ant-steps-item-finish .ant-steps-item-icon {
            background-color: ${token.colorSuccess};
            border-color: ${token.colorSuccess};
          }

          .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon {
            color: white;
          }
        `}
        items={[
          {
            title: 'Verbindung zu GIN',
            content:
              step === 0 ? (
                <Space orientation="vertical" size="middle" style={{ padding: '15px 0 15px 0', width: '100%' }}>
                  <EcardConnectionCheck connectionState={connectionState} onConnectionStateChanged={setConnectionState} />
                  <Button disabled={connectionState !== 'ok'} type="primary" onClick={loadCardReaders} loading={cardReadersLoading}>
                    Weiter
                  </Button>
                  {cardReadersError && <Typography.Text type="danger">{cardReadersError}</Typography.Text>}
                </Space>
              ) : (
                <Typography.Text type="secondary">
                  Das Gesundheits-Informations-Netz (GIN) ist von Ihrem Standort erreichbar
                </Typography.Text>
              ),
          },
          {
            title: 'Kartenlesegerät auswählen',
            content:
              step === 1 ? (
                <Space orientation="vertical" size="middle" style={{ padding: '15px 0 15px 0' }}>
                  Bitte wählen Sie ein Kartenlesegerät (GINO) aus, in das Sie ihre Admin-Karte (o-card) stecken werden:
                  {cardReaders.length > 0 ? (
                    <Radio.Group
                      onChange={e => {
                        const cardReader = cardReaders.find(it => it.id === e.target.value);
                        if (cardReader) {
                          setCardReader(cardReader);
                        }
                      }}
                      value={cardReader?.id ?? ''}
                    >
                      <Space orientation="vertical">
                        {cardReaders.map(cr => (
                          <Radio key={cr.id} value={cr.id}>
                            <strong>{cr.id}</strong>{' '}
                            <Typography.Text type="secondary">
                              IP: {cr.checkIp} Firmware: {cr.firmwareVersion}
                            </Typography.Text>
                          </Radio>
                        ))}
                      </Space>
                    </Radio.Group>
                  ) : (
                    <Typography.Text type="danger">Es konnten keine Kartenlesegeräte für Ihren Anschluss gefunden werden</Typography.Text>
                  )}
                  <Space>
                    <Button onClick={() => setStep(step => step - 1)}>Zurück</Button>
                    <Button disabled={!cardReader} type="primary" onClick={checkCardReaderConnection}>
                      Weiter
                    </Button>
                  </Space>
                  {cardReaderConnectionError && <Typography.Text type="danger">{cardReaderConnectionError}</Typography.Text>}
                </Space>
              ) : (
                step > 1 && (
                  <Typography.Text type="secondary">
                    Die Verbindung zum Lesegerät {cardReader?.id} wurde erfolgreich hergestellt
                  </Typography.Text>
                )
              ),
          },
          {
            title: 'Admin-Karte stecken',
            content:
              step === 2 ? (
                <Space orientation="vertical" size="middle" style={{ padding: '15px 0 15px 0' }}>
                  <div>
                    Bitte stecken Sie jetzt Ihre Admin-Karte in das Kartenlesegerät <strong>{cardReader?.id}</strong> und klicken Sie auf
                    "Kartendaten auslesen":
                  </div>
                  <Button type="primary" onClick={readCardData}>
                    Kartendaten auslesen
                  </Button>
                  {cardData && (
                    <table>
                      <tbody>
                        <tr>
                          <td>Name:</td>
                          <td>
                            <strong>{cardData.name}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td>Nummer:</td>
                          <td>
                            <strong>{cardData.nummer}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                  {cardReaderDataError && <Typography.Text type="danger">{cardReaderDataError}</Typography.Text>}
                  <Space>
                    <Button onClick={() => setStep(step => step - 1)}>Zurück</Button>
                    <Button
                      disabled={!cardData}
                      type="primary"
                      onClick={() => {
                        setStep(step => step + 1);
                        setPin('');
                        setPinError('');
                      }}
                    >
                      Weiter
                    </Button>
                  </Space>
                </Space>
              ) : (
                step > 2 && (
                  <Typography.Text type="secondary">
                    Die Daten der Admin-Karte wurden erfolgreich gelesen: {cardData?.nummer}, {cardData?.name}
                  </Typography.Text>
                )
              ),
          },
          {
            title: 'Admin-PIN angeben',
            content:
              step === 3 ? (
                <Space orientation="vertical" size="middle" style={{ padding: '15px 0 15px 0' }}>
                  Geben Sie bitte den PIN-Code für die gesteckte Admin-Karte ein:
                  <Input.Password
                    placeholder="1234"
                    value={pin}
                    onChange={e => {
                      setPin(e.target.value);
                    }}
                    maxLength={4}
                    style={{ width: '80px', marginLeft: '2px' }}
                  />
                  <Space>
                    <Button onClick={() => setStep(step => step - 1)}>Zurück</Button>
                    <Button
                      disabled={pin.length !== 4 || !/^\d+$/.test(pin)}
                      type="primary"
                      onClick={() => readCardDataWithPin()}
                      loading={ordisLoading}
                    >
                      Weiter
                    </Button>
                  </Space>
                  {pinError && <Typography.Text type="danger">{pinError}</Typography.Text>}
                  <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                    <strong>Achtung:</strong> Die Admin-Karte (o-card) wird gesperrt, wenn der PIN 3-mal falsch eingegeben wurde. Die Karte
                    kann dann nur noch über den PUK auf der{' '}
                    <a onClick={() => openExternalLink('https://services.ecard.sozialversicherung.at/')}>e-card Weboberfläche</a> entsperrt
                    werden.
                  </Typography.Paragraph>
                </Space>
              ) : (
                step > 3 && (
                  <Typography.Text type="secondary">
                    Der eingegebene PIN-Code ist valide. Ihre Vertragspartnernummer (VPNR) lautet {vpnr}
                  </Typography.Text>
                )
              ),
          },
          {
            title: 'Stammdaten verknüpfen',
            content: step === 4 && (
              <Space orientation="vertical" size="middle" style={{ padding: '15px 0 15px 0' }}>
                Bitte wählen Sie den passenden Standort Ihrer Ordination mit dem dazugehörigen Tätigkeitsbereich aus, an dem sich das
                Kartenlesegerät befindet:
                {ordis.length > 0 ? (
                  <Radio.Group
                    onChange={e => {
                      const ordi = ordis.find(it => it.ordinationId === e.target.value);
                      if (ordi) {
                        setOrdi(ordi);
                      }
                    }}
                    value={ordi?.ordinationId ?? ''}
                  >
                    <Space orientation="vertical">
                      {ordis.map(ordi => (
                        <Radio key={ordi.ordinationId} value={ordi.ordinationId}>
                          <strong>
                            {ordi.strasse}, {ordi.postleitzahl} {ordi.stadt}
                          </strong>{' '}
                          <Typography.Text type="secondary">ID: {ordi.ordinationId}</Typography.Text>
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                ) : (
                  <Typography.Text type="danger">
                    Es konnten keine Standortdaten der Ordinationen für Ihren Anschluss gefunden werden
                  </Typography.Text>
                )}
                {ordi && (
                  <Select
                    placeholder="Tätgkeitsbereich auswählen"
                    style={{ width: '250px', marginLeft: '2px' }}
                    onChange={setTaetigkeitsbereichId}
                    value={taetigkeitsbereichId}
                    options={ordi?.taetigkeitsBereich?.map(it => ({ value: it.id, label: it.anzeigeText })) ?? []}
                  />
                )}
                {taetigkeitsbereichId && (
                  <Input
                    placeholder="Optional: Suffix für Bezeichnung"
                    style={{ width: '250px', marginLeft: '2px' }}
                    onChange={e => setNameSuffix(e.target.value)}
                    value={nameSuffix}
                  />
                )}
                <Space>
                  <Button onClick={() => setStep(step => step - 1)}>Zurück</Button>
                  <Button
                    disabled={!ordi || !taetigkeitsbereichId}
                    type="primary"
                    onClick={createEcardConfig}
                    loading={createEcardConfigLoading}
                  >
                    Abschließen
                  </Button>
                </Space>
              </Space>
            ),
          },
        ]}
      />
    </MainContent>
  );
};
